import { useEffect, useMemo } from 'react';

import { useFormik } from 'formik';
import { get, set, cloneDeep } from 'lodash';

import createInitialValues from './createInitialValues';
import createValidationSchema from './createValidationSchema';
import formatServerData from './formatServerData';

const formatValue = (type, key, value, adjustedValues) => {
  if (!value) {
    return;
  }

  if (type === 'percentage') {
    set(adjustedValues, key, value / 100);
  } else if (type === 'currency') {
    set(adjustedValues, key, value.toString()?.replace?.(/,/g, ''));
  }
};

function formatValuesOnSubmit(values, fields) {
  const adjustedValues = cloneDeep(values);
  const flattenedFields = fields.map((field) => field.fields).flat();

  const fieldKeys = flattenedFields.map((field) => field.name);

  const format = (key) => {
    // Find the corresponding field object
    const field = flattenedFields.find((f) => f.name === key);

    if (!field) {
      return;
    }

    if (field && field.innerFields) {
      const value = get(adjustedValues, key);
      if (!value || (Array.isArray(value) && value[0] === null)) {
        return;
      }
      value.forEach((v, i) => {
        Object.keys(v).forEach((k) => {
          const keyPath = `${key}[${i}].${k}`;
          const fld = field.innerFields.find((f) => f.name === k);
          formatValue(fld?.type, keyPath, v[k], adjustedValues);
        });
      });
      return;
    }

    const value = get(adjustedValues, key);

    formatValue(field?.type, key, value, adjustedValues);
  };

  // Iterate over each key in the values object
  fieldKeys.forEach((key) => format(key));

  return adjustedValues;
}

export default function useForm({
  fields,
  onSubmit,
  values,
  onlyValuesPresentInForm,
  onValuesChange,
  // updateOnValuesChange = false,
}) {
  const initialValues = useMemo(() => createInitialValues(fields), [fields]);

  // const hasValues = values && Object.keys(values).length > 0;

  const validationSchema = useMemo(
    () => createValidationSchema(fields),
    [fields]
  );

  const valuesPresentInForm = {};

  Object.keys(initialValues).forEach((key) => {
    valuesPresentInForm[key] = values?.[key];
  });

  const formik = useFormik({
    initialValues: values
      ? formatServerData(
          onlyValuesPresentInForm ? valuesPresentInForm : values,
          fields
        )
      : initialValues,
    validationSchema,
    onSubmit: (formValues, actions) => {
      return onSubmit(formatValuesOnSubmit(formValues, fields), actions);
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(formik.values, formik.setFieldValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, onValuesChange]);

  // Update on values change
  /*  useEffect(() => {
    if (updateOnValuesChange && formik.dirty && hasValues) {
      onSubmit(formatValuesOnSubmit(formik.values, fields));
    }
  }, [
    formik.values,
    formik.dirty,
    updateOnValuesChange,
    fields,
    hasValues,
    onSubmit,
  ]); */

  return {
    formik,
    fields,
    values,
  };
}
